/**
 * hotjar-order-link.js — stamp the shopper's Hotjar User ID on the cart.
 *
 * Hotjar cannot run inside Shopify checkout (sandboxed; no third-party
 * scripts), so recordings never see the purchase. To tie an order back to its
 * recordings we copy the Hotjar User ID into a PRIVATE cart attribute
 * (`__hj_user_id`, double underscore → hidden from the storefront/checkout,
 * shown on the Admin order). In Hotjar, paste it into Recordings → filter →
 * User ID to watch what that buyer did.
 *
 * Consent: Hotjar only loads via the consent-gated GTM tag, so no Hotjar ID
 * exists without consent. As a second gate we also honour Shopify's
 * customerPrivacy API when it is present. The ID is pseudonymous.
 *
 * Cart safety: Shopify recommends not racing cart requests, so we debounce
 * well past any in-flight add/update (the builder already sends the ID in its
 * own attribute call), read /cart.js first, and only POST an attribute-only
 * /cart/update.js when the cart has items and the stored ID differs.
 *
 * Loaded (deferred) by sections/cart-drawer.liquid and sections/main-cart.liquid.
 * Pinned by tests/hotjar-order-link.test.js.
 */
(function (win) {
  'use strict';

  if (!win || win.MD_hotjarOrderLink) return;

  var ATTR_KEY = '__hj_user_id';
  var SETTLE_MS = 2500; // after drawer open / last cart:updated
  var RETRY_MS = 2000; // Hotjar loads async via GTM after consent
  var MAX_ATTEMPTS = 5;
  var ID_RE = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

  // Hotjar's documented accessor. Returns '' when Hotjar hasn't loaded
  // (no consent, blocked, or not yet) or the value looks wrong.
  function getHotjarUserId() {
    try {
      var hj = win.hj;
      if (!hj || !hj.globals || typeof hj.globals.get !== 'function') return '';
      var id = hj.globals.get('userId');
      return typeof id === 'string' && ID_RE.test(id) ? id.toLowerCase() : '';
    } catch (e) {
      return '';
    }
  }

  function analyticsAllowed() {
    try {
      var cp = win.Shopify && win.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {
      /* fall through: Hotjar's own consent gate still applies */
    }
    return true;
  }

  function routes() {
    var r = (win.FoxTheme && win.FoxTheme.routes) || {};
    return {
      cart: (r.cart_url || '/cart') + '.js',
      update: r.cart_update_url || '/cart/update.js',
    };
  }

  function postConfig(body) {
    var cfg =
      win.FoxTheme && win.FoxTheme.utils && typeof win.FoxTheme.utils.fetchConfig === 'function'
        ? win.FoxTheme.utils.fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return Object.assign({}, cfg, { body: body });
  }

  var inFlight = false;

  // Resolves to 'stamped' | 'current' | 'empty' | 'no-id' | 'denied' | 'error'.
  function stampNow() {
    if (!analyticsAllowed()) return Promise.resolve('denied');
    var id = getHotjarUserId();
    if (!id) return Promise.resolve('no-id');
    if (inFlight || typeof win.fetch !== 'function') return Promise.resolve('error');
    inFlight = true;
    var r = routes();
    return win
      .fetch(r.cart, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !cart.item_count) return 'empty';
        var attrs = cart.attributes || {};
        if (attrs[ATTR_KEY] === id) return 'current';
        var payload = { attributes: {} };
        payload.attributes[ATTR_KEY] = id;
        return win.fetch(r.update, postConfig(JSON.stringify(payload))).then(function (res) {
          return res && res.ok ? 'stamped' : 'error';
        });
      })
      .catch(function () {
        return 'error';
      })
      .then(function (result) {
        inFlight = false;
        if (win.MD_DEBUG_BUILDER === true && win.console) win.console.log('[hotjar-order-link]', result);
        return result;
      });
  }

  var timer = null;
  var attempts = 0;

  function run() {
    timer = null;
    stampNow().then(function (result) {
      // Hotjar may still be loading behind GTM/consent; retry a few times.
      if (result === 'no-id' && ++attempts < MAX_ATTEMPTS) schedule(RETRY_MS, true);
    });
  }

  function schedule(delay, isRetry) {
    if (!isRetry) attempts = 0;
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, delay);
  }

  function isDrawerOpen() {
    var drawer = win.document && win.document.querySelector('cart-drawer');
    return !!(drawer && drawer.hasAttribute('open'));
  }

  function init() {
    var doc = win.document;
    if (!doc) return;

    var drawer = doc.querySelector('cart-drawer');
    if (drawer && typeof win.MutationObserver !== 'undefined') {
      new win.MutationObserver(function () {
        if (drawer.hasAttribute('open')) schedule(SETTLE_MS);
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    // Push the stamp past any cart mutation still settling while the cart is shown.
    doc.addEventListener('cart:updated', function () {
      if (timer || isDrawerOpen() || doc.querySelector('main-cart')) schedule(SETTLE_MS);
    });

    if (doc.querySelector('main-cart')) schedule(SETTLE_MS);
  }

  // Consent-checked ID for callers that already write cart attributes (the
  // builder adds it to its own /cart/update.js call instead of racing ours).
  function currentId() {
    return analyticsAllowed() ? getHotjarUserId() : '';
  }

  win.MD_hotjarOrderLink = { stampNow: stampNow, currentId: currentId, ATTR_KEY: ATTR_KEY };
  init();
})(typeof window !== 'undefined' ? window : null);
